import React, { useCallback, useMemo, useState } from 'react';
import { StyleSheet, View, } from 'react-native';
import Text from 'design-system/Text';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { useScrollableModalMaxHeight } from 'hooks';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import { sortAccountsByType } from 'utils/api/sortAccounts';
import { TextButton } from 'design-system/TextButton';
import { Switch } from 'design-system/Switch';
import useColors from 'hooks/useColors';
import Modal from 'utils/packages/Modal';
import TouchableHighlight from 'utils/packages/TouchableHighlight';
import { AccountItem } from 'features/spaces/components';
import { ModalFlatList } from './ModalScrollables';
import { isWeb } from '../constants';
import ListEmptyView from './ListEmptyView';
import RadioButton from './RadioButton';
export const getAccountsTitle = (currentAccountIds, excluded) => {
    if (currentAccountIds) {
        return `${excluded ? 'Hidden' : 'Accounts'} (${currentAccountIds.length})`;
    }
    return 'All Accounts';
};
const keyExtractor = (item) => item.id.toString();
const MultiSelectAccountContent = ({ accounts, alreadySelected, onSelected, isMultipleSelect = true, showExcludedTxns, showSelectAll, onScroll, showHiddenSection = false, }) => {
    const colors = useColors();
    const [selectedAccounts, setSelectedAccounts] = useState(alreadySelected);
    const [showExcludedTransactions, setShowExcludedTransactions] = useState(showExcludedTxns ?? true);
    const onPress = useCallback((data) => {
        if (!isMultipleSelect) {
            setSelectedAccounts([data.id]);
            return;
        }
        let array = [...selectedAccounts];
        const index = array.indexOf(data.id);
        if (index === -1) {
            array.push(data.id);
        }
        else {
            array = array.filter((item) => item !== data.id);
        }
        setSelectedAccounts(array);
    }, [isMultipleSelect, selectedAccounts]);
    const onPressSave = useCallback(() => {
        MultiSelectAccountModal.hide();
        onSelected(selectedAccounts, showExcludedTransactions);
    }, [onSelected, selectedAccounts, showExcludedTransactions]);
    const handleToggle = useCallback((value) => {
        setShowExcludedTransactions(value);
    }, []);
    const sortedAccounts = useMemo(() => {
        const sorted = sortAccountsByType([...accounts]);
        const hidden = [];
        const nonHidden = [];
        for (const acc of sorted) {
            if (acc?.isHidden)
                hidden.push(acc);
            else
                nonHidden.push(acc);
        }
        return { sorted, hidden, nonHidden };
    }, [accounts]);
    const { sorted, hidden: hiddenAccounts, nonHidden: nonHiddenAccounts, } = sortedAccounts;
    const sectionedAccounts = useMemo(() => {
        const data = [];
        if (nonHiddenAccounts.length > 0) {
            data.push({
                type: 'header',
                id: 'non-hidden',
                excluded: false,
            });
            data.push(...nonHiddenAccounts);
        }
        if (hiddenAccounts.length > 0) {
            data.push({
                type: 'header',
                id: 'hidden',
                excluded: true,
            });
            data.push(...hiddenAccounts);
        }
        return data;
    }, [nonHiddenAccounts, hiddenAccounts]);
    const sectionHeader = useCallback((excluded) => {
        const accounts = excluded ? hiddenAccounts : nonHiddenAccounts;
        const selectedAccountIds = accounts
            .filter((acc) => selectedAccounts.includes(acc.id))
            .map((acc) => acc.id);
        const isAllSelected = selectedAccountIds.length ===
            (excluded ? hiddenAccounts.length : nonHiddenAccounts.length);
        return (<View style={staticStyles.selectAllRow}>
          <Text Text-16 Secondary>
            {getAccountsTitle(selectedAccountIds, excluded)}
          </Text>
          <TextButton text={`${isAllSelected ? 'Deselect' : 'Select'} all`} onPress={() => {
                if (isAllSelected) {
                    const excludedIds = new Set(selectedAccountIds);
                    const result = selectedAccounts.filter((id) => !excludedIds.has(id));
                    setSelectedAccounts(result);
                }
                else {
                    const resultSet = new Set([
                        ...selectedAccounts,
                        ...accounts.map((acc) => acc.id),
                    ]);
                    const result = Array.from(resultSet);
                    setSelectedAccounts(Array.from(result));
                }
            }}/>
        </View>);
    }, [hiddenAccounts, nonHiddenAccounts, selectedAccounts]);
    const ListHeaderComponent = useMemo(() => {
        if (!sorted.length ||
            (!hiddenAccounts.length && !nonHiddenAccounts.length) ||
            !showSelectAll)
            return null;
        const isAllSelected = selectedAccounts.length >=
            accounts.filter((acc) => !acc?.isHidden).length;
        return (<>
        {typeof showExcludedTxns === 'boolean' && (<View style={staticStyles.row}>
            <Text Text-16 flex>
              Show excluded transactions
            </Text>
            <Switch onValueChange={handleToggle} value={showExcludedTransactions}/>
          </View>)}
        {!showHiddenSection && (<View style={staticStyles.selectAllRow}>
            <Text Text-16 color={colors.text.tertiary}>
              {getAccountsTitle(selectedAccounts)}
            </Text>
            <TextButton text={`${isAllSelected ? 'Deselect' : 'Select'} all`} onPress={() => {
                    if (isAllSelected) {
                        setSelectedAccounts([]);
                    }
                    else {
                        const allAccounts = accounts.flatMap((acc) => !acc.isHidden ? [acc.id] : []);
                        setSelectedAccounts(allAccounts);
                    }
                }}/>
          </View>)}
      </>);
    }, [
        sorted.length,
        hiddenAccounts.length,
        nonHiddenAccounts.length,
        showSelectAll,
        selectedAccounts,
        accounts,
        showExcludedTxns,
        handleToggle,
        showExcludedTransactions,
        showHiddenSection,
        colors.text.tertiary,
    ]);
    const renderItem = useCallback(({ item }) => {
        if (item.type === 'header') {
            return sectionHeader(item.excluded);
        }
        const selected = selectedAccounts.indexOf(item.id) > -1;
        return (<TouchableHighlight style={staticStyles.touchable} onCard hoverOnLight activeOpacity={1} onPress={() => onPress(item)} underlayColor={colors.background.underlay}>
          <View style={staticStyles.accountRow} pointerEvents="none">
            <RadioButton type={isMultipleSelect ? 'multiple' : 'single'} isChecked={selected}/>
            <View style={staticStyles.accountItem}>
              <AccountItem account={item} disabled/>
            </View>
          </View>
        </TouchableHighlight>);
    }, [
        colors.background.underlay,
        isMultipleSelect,
        onPress,
        sectionHeader,
        selectedAccounts,
    ]);
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING - rem(16));
    const ListEmptyComponent = useMemo(() => <ListEmptyView title="No accounts to choose from" onCard compact/>, []);
    const maxHeight = useScrollableModalMaxHeight();
    const style = useMemo(() => ({ maxHeight }), [maxHeight]);
    return (<>
      <ModalFlatList data={showHiddenSection ? sectionedAccounts : sorted} onScroll={onScroll} renderItem={renderItem} scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} keyExtractor={keyExtractor} extraData={selectedAccounts} style={style} ListHeaderComponent={ListHeaderComponent} ListEmptyComponent={ListEmptyComponent} contentContainerStyle={contentContainerStyle}/>
      <Button brand floating title="Save" onPress={onPressSave} containerStyle={staticStyles.buttonContainer} disabled={!selectedAccounts.length}/>
    </>);
};
const MultiSelectAccountModal = {
    show: (props) => {
        Modal.show(<MultiSelectAccountContent {...props}/>, {
            containerStyle: staticStyles.modal,
            title: 'Select accounts',
            titleBottomBorder: true,
            scrollable: {
                scrollValue: props.scrollValue,
            },
        });
    },
    hide: Modal.hide,
};
const staticStyles = StyleSheet.create({
    modal: {
        paddingHorizontal: 0,
        overflow: 'hidden',
    },
    buttonContainer: {
        marginHorizontal: 0,
    },
    row: {
        flexDirection: 'row',
        paddingTop: rem(16),
        paddingHorizontal: rem(16),
    },
    selectAllRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingTop: rem(16),
        paddingBottom: rem(16),
        justifyContent: 'space-between',
        paddingHorizontal: rem(16),
        ...(isWeb && {
            paddingBottom: rem(8),
            paddingRight: rem(4),
        }),
    },
    accountRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginLeft: isWeb ? rem(8) : rem(16),
    },
    accountItem: {
        flex: 1,
        ...(isWeb && { marginLeft: rem(8) }),
    },
    touchable: {
        ...(isWeb && { marginHorizontal: rem(8) }),
    },
});
export default MultiSelectAccountModal;
